import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';
import ActionButtons from '@/components/feature/ActionButtons';
import { getMovieById } from '@/utils/media';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function MovieDetail() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);

  usePageMeta({
    title: movie ? `${movie.title} - 河北彩花影视作品详情` : '未找到作品 - 河北彩花影视大全',
    description: movie
      ? `${movie.title}是${movie.year}年${movie.type}，由${movie.director}执导，${movie.cast.join('、')}主演。${movie.summary}`
      : '抱歉，未找到对应的影视作品信息。',
    keywords: movie ? `${movie.title},${movie.genres.join(',')},河北彩花影视作品` : '河北彩花影视作品',
  });

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (item) =>
          item.id !== movie.id &&
          (item.genres.some((genre) => movie.genres.includes(genre)) || item.year === movie.year),
      )
      .sort((a, b) => b.hot - a.hot)
      .slice(0, 4);
  }, [movie]);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </div>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">该作品可能不存在，或链接已失效。</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回作品列表
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* 主视觉 */}
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={movie.gallery[0] || movie.cover}
          alt={`${movie.title} 作品背景图`}
          title={`${movie.title} 河北彩花影视作品`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/60"></div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <div className="text-background-200">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视作品', to: '/category' }, { label: movie.title }]} />
          </div>
          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="h-[300px] w-[200px] shrink-0 overflow-hidden rounded-lg bg-background-900 md:h-[360px] md:w-[240px]">
              <img
                src={movie.cover}
                alt={`${movie.title} 作品封面`}
                title={`${movie.title} 河北彩花影视作品`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {movie.type}
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100">
                  {movie.year}
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100">
                  {movie.region}
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-100">
                  {movie.duration}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-background-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400"></i>
                  <span className="font-semibold text-background-50">{movie.rating.toFixed(1)}</span>
                  / 10 示范评分
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-accent-400"></i>
                  热度 {movie.hot}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {movie.releaseDate} 上映
                </span>
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">导演：</dt>
                  <dd className="text-background-50">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">主演：</dt>
                  <dd className="text-background-50">{movie.cast.join('、')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-300">类型：</dt>
                  <dd className="text-background-50">{movie.genres.join(' / ')}</dd>
                </div>
              </dl>

              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-background-200">{movie.summary}</p>

              <div className="mt-6">
                <ActionButtons id={`movie-${movie.id}`} title={movie.title} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 简介 + 剧照 */}
      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeader icon="ri-file-text-line" title="剧情简介" subtitle="作品内容介绍" />
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <p className="text-sm leading-loose text-foreground-700">{movie.summary}</p>
              <p className="mt-4 text-sm leading-loose text-foreground-700">
                《{movie.title}》是{movie.year}年上映的{movie.type}作品，由{movie.director}执导，
                {movie.cast.join('、')}主演。影片融合{movie.genres.join('、')}等元素，以{movie.region}为背景展开叙事。
                以上内容为示范演示资料。
              </p>
            </div>

            {movie.gallery.length > 0 && (
              <div className="mt-10">
                <SectionHeader icon="ri-image-2-line" title="剧照欣赏" subtitle="作品精彩画面" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {movie.gallery.map((image, index) => (
                    <div key={image} className="aspect-video w-full overflow-hidden rounded-lg bg-background-200">
                      <img
                        src={image}
                        alt={`${movie.title} 剧照 ${index + 1}`}
                        title={`${movie.title} 河北彩花影视作品剧照`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">作品信息</h3>
              <dl className="mt-4 flex flex-col gap-3 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">作品名称</dt>
                  <dd className="text-right font-medium text-foreground-900">{movie.title}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">作品类型</dt>
                  <dd className="text-right font-medium text-foreground-900">{movie.type}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">上映年份</dt>
                  <dd className="text-right font-medium text-foreground-900">{movie.year}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">上映地区</dt>
                  <dd className="text-right font-medium text-foreground-900">{movie.region}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">片长 / 集数</dt>
                  <dd className="text-right font-medium text-foreground-900">{movie.duration}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">示范评分</dt>
                  <dd className="text-right font-medium text-accent-600">{movie.rating.toFixed(1)} / 10</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">主演明星</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {movie.cast.map((name) => (
                  <span key={name} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <div className="mt-14">
            <SectionHeader icon="ri-movie-2-line" title="相关作品推荐" subtitle="根据类型与上映年份推荐" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}